import classNames from "classnames";
import { isLeft } from "fp-ts/es6/These";
import { inject } from "mobx-react";

import DateDisplay from "../../../../core/components/DateDisplay.component";
import { ShareButtons } from "../../../../core/components/integrations/ShareButtons.component";
import { ResponsiveImage } from "../../../../core/components/ResponsiveImage.component";
import * as Webapi from "@ihr-radioedit/inferno-webapi";
import { generateImageAssetSizes } from "@inferno/renderer-shared-core";
import { placeholderImage } from "@ihr-radioedit/inferno-core";
import { formatImage } from "@ihr-radioedit/inferno-core";
import { getPaths, CoastArticlePayloadResolver } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { DATETIME_DISPLAY_FORMATS } from "@inferno/renderer-shared-core";
import { Container, Heading } from "../../../../core/ui";
import { CoastContentBlock } from "./CoastContent.component";
import { RESPONSIVE_IMAGE } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("CoastContentArticle.component");

export type CoastContentArticleProps = {
  content: Webapi.PublishRecordFragment;
  coastHints: string[];
  store?: Store;
};

export const CoastContentArticle = inject("store")(({ content, store, coastHints }: CoastContentArticleProps) => {
  if (!store) {
    return null;
  }
  const { site, request, env } = store;
  const resolvedPayload = CoastArticlePayloadResolver.decode(content.payload);

  if (isLeft(resolvedPayload)) {
    log.error(`Payload failed validation: ${getPaths(resolvedPayload)}`);
    return null;
  }

  const payload = resolvedPayload.right;
  const { summary, fields } = payload;
  const { pub_start } = content;
  const shareUrl = site.getShareUrl(request);
  const { srcset, sizes } = generateImageAssetSizes(formatImage(summary.image.url, env.IMAGE_HOST));
  const placeholderSrc = `${placeholderImage}?ops=resize(900,506),quality(10)`;

  let videoBlock = null;
  if (fields?.primary_video?.value?.provider) {
    videoBlock = {
      ...fields.primary_video,
    };
  }

  return (
    <Container className={classNames("component-coast-content-article coast-content-layout-container", coastHints)}>
      <Heading level={1}>{summary.title}</Heading>
      <DateDisplay
        datetime={pub_start}
        className="item-published"
        dateFormat={DATETIME_DISPLAY_FORMATS.FULL_MONTH_SHORT_DAY_FULL_YEAR}
      />
      <ShareButtons url={shareUrl} title={summary.title || ""} desc={summary.description || ""} setStyles={false} />

      {!videoBlock ? (
        <figure className="item-thumb">
          <section className="image-container">
            <ResponsiveImage
              alt={summary.image.alt_text || summary.title || ""}
              src={placeholderSrc}
              placeholder={placeholderSrc}
              srcset={srcset}
              sizes={sizes}
              initialWidth={RESPONSIVE_IMAGE.responsiveImageInitialWidth}
              initialHeight={RESPONSIVE_IMAGE.responsiveImageInitialHeight}
            />
          </section>
        </figure>
      ) : null}

      <article>
        {videoBlock ? <CoastContentBlock block={videoBlock} /> : null}
        {fields.blocks.value?.map((block, index) => (
          <CoastContentBlock block={block} key={index} />
        ))}
      </article>
    </Container>
  );
});

export default CoastContentArticle;
